import _React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Copy } from 'lucide-react';
import type { SlideDTO } from '../../../core/types/lesson.types';
import { slideRegistry } from '../../../core/plugin-system/SlideRegistry';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../shared/utils';

interface SlideBuilderProps {
  slides: SlideDTO[];
  selectedSlideId: string | null;
  allowedTypes: string[];
  errors: Record<string, any>;
  onSelectSlide: (id: string) => void;
  onUpdateSlide: (id: string, updates: Partial<SlideDTO>) => void;
  onRemoveSlide: (id: string) => void;
  onDuplicateSlide: (id: string) => void;
  onReorderSlides: (slides: SlideDTO[]) => void;
  isPreview?: boolean;
}

export const SlideBuilder: React.FC<SlideBuilderProps> = ({
  slides,
  selectedSlideId,
  allowedTypes: _allowedTypes,
  errors,
  onSelectSlide,
  onUpdateSlide,
  onRemoveSlide,
  onDuplicateSlide,
  onReorderSlides,
  isPreview = false,
}) => {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(slides);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onReorderSlides(items);
  };

  if (slides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-muted border-2 border-dashed border-border rounded-lg">
        <p className="text-muted-foreground text-center mb-2">
          Nu există slide-uri adăugate
        </p>
        <p className="text-sm text-muted-foreground text-center">
          Folosește butoanele din sidebar pentru a adăuga slide-uri
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Slide-uri ({slides.length})
        </h3>
        {!isPreview && (
          <p className="text-sm text-muted-foreground">
            Trage pentru a reordona slide-urile
          </p>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="slides" isDropDisabled={isPreview}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn(
                'space-y-4',
                snapshot.isDraggingOver && 'bg-primary/10 rounded-lg p-2'
              )}
            >
              {slides.map((slide, index) => {
                const plugin = slideRegistry.get(slide.type!);
                if (!plugin) {
                  return (
                    <div
                      key={slide.id}
                      className="p-4 bg-destructive/10 border border-red-300 rounded-lg"
                    >
                      <p className="text-destructive">
                        Plugin pentru tipul "{slide.type}" nu a fost găsit
                      </p>
                    </div>
                  );
                }

                const isSelected = selectedSlideId === slide.id;
                const slideErrors = errors[`slide.${index}`];

                return (
                  <Draggable
                    key={slide.id}
                    draggableId={slide.id!}
                    index={index}
                    isDragDisabled={isPreview}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={cn(
                          'border rounded-lg bg-card transition-all',
                          isSelected && 'ring-2 ring-blue-500 shadow-lg',
                          !isSelected && 'hover:shadow-md',
                          snapshot.isDragging && 'shadow-2xl rotate-2',
                          slideErrors && 'border-red-300'
                        )}
                        onClick={() => !isPreview && onSelectSlide(slide.id!)}
                      >
                        {/* Slide Header */}
                        <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted rounded-t-lg">
                          {!isPreview && (
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing hover:text-primary"
                            >
                              <GripVertical className="w-5 h-5" />
                            </div>
                          )}

                          <plugin.icon className="w-5 h-5 text-muted-foreground" />

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-foreground">
                                Slide {index + 1}:
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {plugin.displayName}
                              </span>
                            </div>
                            {slide.text && (
                              <p className="text-xs text-muted-foreground truncate mt-1">
                                {slide.text}
                              </p>
                            )}
                          </div>

                          {!isPreview && (
                            <div className="flex items-center gap-1">
                              <Button
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  onDuplicateSlide(slide.id!);
                                }}
                                variant="ghost"
                                size="icon"
                                title="Duplică slide"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  onRemoveSlide(slide.id!);
                                }}
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Șterge slide"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Slide Content */}
                        <div className="p-4">
                          <plugin.CreationComponent
                            slide={slide}
                            errors={slideErrors}
                            onChange={(updates) => onUpdateSlide(slide.id!, updates)}
                            isPreview={isPreview}
                          />
                        </div>

                        {/* Error Indicator */}
                        {slideErrors && (
                          <div className="px-4 pb-3">
                            <div className="p-2 bg-destructive/10 border border-red-200 rounded-sm text-sm text-destructive">
                              {slideErrors}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default SlideBuilder;


